import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import type { FiliationEvent, FiliationNode, FiliationEventWithFiliationStateGroupedByDate } from "@/diagramme/types";

dayjs.extend(customParseFormat);

/**
 * @description -  Hook that group events by dates and compute every filiation state for each of these dates
 *
 * @param {FiliationEvent[]} events - Filiation events related to chosen nodes and their related nodes.
 * @param {FiliationNode[]} originesByDates - Filiation nodes representing the filiation state for each date.
 *
 * @returns {FiliationEventWithFiliationStateGroupedByDate[]} - An array of filiation events grouped by dates containing filiation state for every date and related events.
 */
export const getFiliationEventsGroupedByDates = (
  events: FiliationEvent[],
  originesByDates: Record<string, { nodes?: FiliationNode[] }>
): FiliationEventWithFiliationStateGroupedByDate[] => {
  const filiationEventsGroupedByDate: FiliationEventWithFiliationStateGroupedByDate[] = Object.entries(
    originesByDates ?? {}
  )
    .filter(([, state]) => Array.isArray(state?.nodes) && state.nodes.length > 0)
    .map(([date, state]) => {
      const filiationState = state.nodes as FiliationNode[];
      const eventsByDate = events
        .filter((event: FiliationEvent) => event.date_event === date && !!event.event_descripion)
        .map((event: FiliationEvent) => ({
          ...event,
          filiationState: [...filiationState],
        }));

      return {
        date,
        events: eventsByDate,
      };
    });

  const filiationEventsSortedByDate = filiationEventsGroupedByDate.sort((a, b) => {
    return dayjs(b.date, "DD/MM/YYYY").valueOf() - dayjs(a.date, "DD/MM/YYYY").valueOf();
  });

  return filiationEventsSortedByDate;
};
